import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Zap, ShieldCheck, Download, Award, Heart, Star } from "lucide-react";

export default function ProductDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [rating, setRating] = useState(5);
  const { add } = useCart();
  const { user } = useAuth();

  const load = () => api.get(`/products/${slug}`).then((r) => setP(r.data));
  useEffect(() => { load(); }, [slug]);

  const addToCart = () => { add(p); toast.success("Added to cart"); };
  const addWish = async () => {
    if (!user) return toast.error("Please login first");
    await api.post(`/wishlist/${p.id}`);
    toast.success("Added to wishlist");
  };
  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) return toast.error("Please login to review");
    try { await api.post("/reviews", { product_id: p.id, rating, comment: reviewText }); toast.success("Review submitted for moderation"); setReviewText(""); }
    catch { toast.error("Failed to submit"); }
  };

  if (!p) return <div className="container-luxe py-32 text-center text-gray-500">Loading…</div>;
  const price = p.sale_price || p.price;
  const gallery = [...(p.images || []), ...(p.gallery || [])].filter(Boolean);

  return (
    <div className="container-luxe py-16">
      <div className="grid lg:grid-cols-2 gap-16">
        <div>
          <div className="aspect-square bg-[#121212] rounded-2xl overflow-hidden">
            {gallery[activeImg] && <img data-testid="product-main-image" src={gallery[activeImg]} alt={p.name} className="w-full h-full object-cover" />}
          </div>
          {gallery.length > 1 && (
            <div className="flex gap-3 mt-4">
              {gallery.map((g, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`w-20 h-20 rounded-lg overflow-hidden border ${activeImg === i ? "border-[#D4AF37]" : "border-transparent"}`}>
                  <img src={g} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-3">{p.category_slug?.replace(/-/g, " ")}</div>
          <h1 data-testid="product-name" className="font-display text-4xl lg:text-5xl font-semibold tracking-tight">{p.name}</h1>
          <div className="flex items-center gap-3 mt-4">
            <div className="flex gap-1 text-[#D4AF37]">{Array.from({ length: 5 }).map((_, i) => <Star key={i} size={16} fill={i < Math.round(p.avg_rating || 0) ? "#D4AF37" : "transparent"} />)}</div>
            <span className="text-gray-500 text-sm">{p.reviews?.length || 0} reviews</span>
          </div>
          <div className="flex items-baseline gap-4 mt-6">
            <span data-testid="product-price" className="font-display text-4xl text-[#D4AF37] font-semibold">${price.toFixed(2)}</span>
            {p.sale_price && <span className="text-gray-500 line-through">${p.price.toFixed(2)}</span>}
          </div>
          <p className="text-gray-300 leading-relaxed mt-6">{p.description}</p>
          <div className="grid grid-cols-2 gap-3 mt-8">
            {[{ i: Zap, t: "Instant Delivery" }, { i: ShieldCheck, t: "Secure Checkout" }, { i: Download, t: "Digital Product" }, { i: Award, t: "Licensed Seller" }].map((f) => (
              <div key={f.t} className="flex items-center gap-3 glass rounded-lg p-4"><f.i size={16} className="text-[#D4AF37]" /> <span className="text-sm">{f.t}</span></div>
            ))}
          </div>
          <div className="flex gap-3 mt-8">
            <button data-testid="btn-add-to-cart" onClick={addToCart} className="btn-gold flex-1">Add to Cart</button>
            <button data-testid="btn-wishlist" onClick={addWish} className="btn-ghost"><Heart size={18} /></button>
          </div>
          {p.sku && <div className="text-xs text-gray-500 mt-6 tracking-widest uppercase">SKU: {p.sku}</div>}
        </div>
      </div>

      {/* REVIEWS */}
      <section className="mt-24">
        <h2 className="font-display text-3xl font-semibold mb-8">Reviews</h2>
        {p.reviews?.length > 0 ? (
          <div className="space-y-4 mb-10">
            {p.reviews.map((r) => (
              <div key={r.id} className="glass rounded-xl p-6">
                <div className="flex justify-between">
                  <div className="font-medium">{r.user_name}</div>
                  <div className="flex gap-1 text-[#D4AF37]">{Array.from({ length: r.rating }).map((_, i) => <Star key={i} size={14} fill="#D4AF37" />)}</div>
                </div>
                <p className="text-gray-400 mt-3">{r.comment}</p>
              </div>
            ))}
          </div>
        ) : <p className="text-gray-500 mb-10">Be the first to review.</p>}

        <form onSubmit={submitReview} className="glass rounded-xl p-6 max-w-xl">
          <h3 className="font-display text-lg mb-4">Write a review</h3>
          <div className="mb-4">
            <label className="block text-sm text-gray-400 mb-2">Rating</label>
            <div className="flex gap-1">
              {[1,2,3,4,5].map((n) => (
                <button key={n} type="button" onClick={() => setRating(n)}>
                  <Star size={22} className={n <= rating ? "text-[#D4AF37]" : "text-gray-600"} fill={n <= rating ? "#D4AF37" : "transparent"} />
                </button>
              ))}
            </div>
          </div>
          <textarea data-testid="review-comment" required rows={4} value={reviewText} onChange={(e) => setReviewText(e.target.value)} placeholder="Share your experience…" />
          <button data-testid="review-submit" className="btn-gold mt-4">Submit Review</button>
        </form>
      </section>
    </div>
  );
}
